import { SizeUnitType } from '@/utils/unit-kit';
import {
    BaseLayerViewType,
    IBaseOperateData,
    IBaseOperateFlag,
    ILayerProps,
    IPoint,
    IStageResizeData,
    StageType,
} from '@/version-types';
import { EventEmitter } from './libs/event-emitter';

class EventBus {
    eventEmit: EventEmitter = new EventEmitter();

    /**
     * 监听唯一事件
     *
     * @param eventName - 事件名称
     * @param callback - 回调函数
     */
    onUnique(eventName: string, callback: (...args: any[]) => void) {
        return this.eventEmit.onUnique(eventName, callback);
    }
    /**
     * 监听事件
     *
     * @param eventName - 事件名称
     * @param callback - 回调函数
     * @param weight - 可选；权重，权重越大，越先执行；默认值为 `0`
     */
    on(eventName: string, callback: Function, weight = 0) {
        return this.eventEmit.on(eventName, callback, weight);
    }

    /** 清除事件 */
    off(handle: number) {
        this.eventEmit.off(handle);
    }

    /** 触发事件 */
    emit(eventName: string, ...args: any) {
        this.eventEmit.emit(eventName, ...args);
    }
}

/** 核心编辑器的全局事件绑定 */
export class Events {
    /** 事件总线 */
    private eventBus: EventBus;

    constructor() {
        this.eventBus = new EventBus();
    }

    /** 获取重新渲染图层的事件名称 */
    private getRerenderLayerEventName(layerId: string) {
        return `gs-rerender-layer-${layerId}`;
    }
    /** 监听需要重新渲染的图层 */
    onRerenderLayer(layerId: string, callback: (layerId: string) => void, weight = 0) {
        return this.eventBus.on(this.getRerenderLayerEventName(layerId), callback, weight);
    }
    /** 触发需要重新渲染的图层 */
    emitRerenderLayer(layerId: string) {
        this.eventBus.emit(this.getRerenderLayerEventName(layerId), layerId);
    }

    /** 获取重新渲染舞台的事件名称 */
    private getRerenderStageEventName(originIndex: number) {
        return `gs-rerender-stage-${originIndex}`;
    }
    /** 监听需要重新渲染的舞台 */
    onRerenderStage(originIndex: number, callback: (originIndex: number) => void, weight = 0) {
        return this.eventBus.on(this.getRerenderStageEventName(originIndex), callback, weight);
    }
    /** 触发需要重新渲染的舞台 */
    emitRerenderStage(originIndex: number) {
        this.eventBus.emit(this.getRerenderStageEventName(originIndex), originIndex);
    }

    /** 获取重新渲染所有舞台的事件名称 */
    private getRerenderSortEventName() {
        return `gs-rerender-sort`;
    }
    /** 监听重新渲染所有舞台 */
    onRerenderAll(callback: (renderStageList: number[]) => void, weight = 0) {
        return this.eventBus.on(this.getRerenderSortEventName(), callback, weight);
    }
    /** 触发重新渲染所有舞台 */
    emitRerenderAll(renderStageList: number[]) {
        this.eventBus.emit(this.getRerenderSortEventName(), [...renderStageList]);
    }

    /** 获取舞台尺寸变化的事件名称 */
    private getStageResizeEventName() {
        return `gs-stage-size-change`;
    }
    /** 监听舞台尺寸变化 */
    onStageResize(callback: (args: IStageResizeData[], isRerenderAll: boolean) => void, weight = 0) {
        return this.eventBus.on(this.getStageResizeEventName(), callback, weight);
    }
    /** 触发舞台尺寸变化 */
    emitStageResize(args: IStageResizeData[], isRerenderAll: boolean) {
        this.eventBus.emit(this.getStageResizeEventName(), [...args], isRerenderAll);
    }

    /** 获取鼠标移动事件名称 */
    private getMouseMoveEventName() {
        return `gs-mouse-move`;
    }
    /** 触发鼠标移动事件 */
    emitMouseMove(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        this.eventBus.emit(this.getMouseMoveEventName(), ev);
    }
    /** 监听鼠标移动事件 */
    onMouseMove(callback: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => void, weight = 0) {
        return this.eventBus.on(this.getMouseMoveEventName(), callback, weight);
    }

    private getMouseDownEventName() {
        return `gs-mouse-down`;
    }
    /** 触发鼠标按下事件 */
    emitMouseDown(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        this.eventBus.emit(this.getMouseDownEventName(), ev);
    }
    /** 监听鼠标按下事件 */
    onMouseDown(callback: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => void, weight = 0) {
        return this.eventBus.on(this.getMouseDownEventName(), callback, weight);
    }

    private getMouseUpEventName() {
        return `gs-mouse-up`;
    }
    /** 触发鼠标抬起事件 */
    emitMouseUp(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        this.eventBus.emit(this.getMouseUpEventName(), ev);
    }
    /** 监听鼠标抬起事件 */
    onMouseUp(callback: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => void, weight = 0) {
        return this.eventBus.on(this.getMouseUpEventName(), callback, weight);
    }

    private getMouseEnterEventName() {
        return `gs-mouse-enter`;
    }
    /** 监听鼠标进入事件 */
    onMouseEnter(callback: (originIndex: number, force?: boolean) => void, weight = 0) {
        return this.eventBus.on(this.getMouseEnterEventName(), callback, weight);
    }
    /** 触发鼠标进入事件 */
    emitMouseEnter(originIndex: number, force?: boolean) {
        this.eventBus.emit(this.getMouseEnterEventName(), originIndex, force);
    }

    private getMouseLeaveEventName() {
        return `gs-mouse-leave`;
    }
    /** 监听鼠标离开事件 */
    onMouseLeave(callback: (originIndex: number, force?: boolean) => void, weight = 0) {
        return this.eventBus.on(this.getMouseLeaveEventName(), callback, weight);
    }
    /** 触发鼠标离开事件 */
    emitMouseLeave(originIndex: number, force?: boolean) {
        this.eventBus.emit(this.getMouseLeaveEventName(), originIndex, force);
    }

    private getContextMenuEventName() {
        return `gs-context-menu`;
    }
    /** 触发右键菜单事件 */
    emitContextMenu(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        this.eventBus.emit(this.getContextMenuEventName(), ev);
    }
    /** 监听右键菜单事件 */
    onContextMenu(callback: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => void, weight = 0) {
        return this.eventBus.on(this.getContextMenuEventName(), callback, weight);
    }

    private getWheelEventName() {
        return `gs-wheel`;
    }
    /** 触发滚轮事件 */
    emitWheel(ev: React.WheelEvent<HTMLDivElement>) {
        this.eventBus.emit(this.getWheelEventName(), ev);
    }
    /** 监听滚轮事件 */
    onWheel(callback: (ev: React.WheelEvent<HTMLDivElement>) => void, weight = 0) {
        return this.eventBus.on(this.getWheelEventName(), callback, weight);
    }

    /** 获取回车事件名称 */
    private getEnterEventName() {
        return `gs-enter`;
    }
    /** 触发回车事件 */
    emitEnter() {
        this.eventBus.emit(this.getEnterEventName());
    }
    /** 监听回车事件 */
    onEnter(callback: () => void, weight = 0) {
        return this.eventBus.on(this.getEnterEventName(), callback, weight);
    }

    private getEscEventName() {
        return `gs-esc`;
    }
    /** 触发esc事件 */
    emitEsc() {
        this.eventBus.emit(this.getEscEventName());
    }
    /** 监听esc事件 */
    onEsc(callback: () => void, weight = 0) {
        return this.eventBus.on(this.getEscEventName(), callback, weight);
    }

    private getDeleteEventName() {
        return `gs-delete`;
    }
    /** 触发删除事件 */
    emitDelete(type?: string) {
        this.eventBus.emit(this.getDeleteEventName(), type);
    }
    /** 监听删除事件 */
    onDelete(callback: (type?: string) => void, weight = 0) {
        return this.eventBus.on(this.getDeleteEventName(), callback, weight);
    }

    private getActiveUpdateEventName() {
        return `gs-active-update`;
    }
    /** 触发激活图层数据更新事件 */
    emitActiveLayerDataUpdate(activeLayer: ILayerProps | null, pickLayer: ILayerProps | null) {
        this.eventBus.emit(this.getActiveUpdateEventName(), activeLayer, pickLayer);
    }
    /** 监听激活图层数据更新事件 */
    onActiveLayerDataUpdate(
        callback: (activeLayer: ILayerProps | null, pickLayer: ILayerProps | null) => void,
        weight = 0,
    ) {
        return this.eventBus.on(this.getActiveUpdateEventName(), callback, weight);
    }

    /** 获取缩放变化的事件名称 */
    private getScaleChangeEventName() {
        return `gs-scale-change`;
    }
    /** 触发缩放变化事件 */
    emitScale(scale: number) {
        this.eventBus.emit(this.getScaleChangeEventName(), scale);
    }
    /** 监听缩放变化事件 */
    onScale(callback: (scale: number) => void, weight = 0) {
        return this.eventBus.on(this.getScaleChangeEventName(), callback, weight);
    }

    private getBaseOperateEventName() {
        return `gs-base-operate`;
    }
    /** 触发基础操作事件 */
    emitBaseOperate(layerId: string, operateType: BaseLayerViewType, args: IBaseOperateFlag, data?: IBaseOperateData) {
        if (operateType === BaseLayerViewType.MOVE || operateType === BaseLayerViewType.ROTATE) {
            this.emitTranslate(layerId, operateType, args, data);
        }
        this.eventBus.emit(this.getBaseOperateEventName(), layerId, operateType, args, data);
    }
    /** 监听基础操作事件 */
    onBaseOperate(
        callback: (
            layerId: string,
            operateType: BaseLayerViewType,
            args: IBaseOperateFlag,
            data?: IBaseOperateData,
        ) => void,
        weight = 0,
    ) {
        return this.eventBus.on(this.getBaseOperateEventName(), callback, weight);
    }

    private getBeforeBaseOperateEventName() {
        return `gs-before-base-operate`;
    }
    /** 触发基础操作触发事件 */
    emitBeforeBaseOperate(layer: ILayerProps, operateType: BaseLayerViewType) {
        this.eventBus.emit(this.getBeforeBaseOperateEventName(), layer, operateType);
    }
    /** 监听基础操作触发事件 */
    onBeforeBaseOperate(callback: (layer: ILayerProps, operateType: BaseLayerViewType) => void, weight = 0) {
        return this.eventBus.on(this.getBeforeBaseOperateEventName(), callback, weight);
    }

    private getBaseOperateChangeEventName() {
        return `gs-base-operate-change`;
    }
    /** 监听基础操作状态变化 */
    onBaseOperateChange(callback: (isBaseOperate: boolean) => void, weight = 0) {
        return this.eventBus.on(this.getBaseOperateChangeEventName(), callback, weight);
    }
    /** 触发基础操作状态变化 */
    emitBaseOperateChange(isBaseOperate: boolean) {
        this.eventBus.emit(this.getBaseOperateChangeEventName(), isBaseOperate);
    }

    private getTranslateEventName() {
        return `gs-translate`;
    }
    /** 监听移动/旋转事件 */
    onTranslate(
        callback: (
            layerId: string,
            operateType: BaseLayerViewType,
            args: IBaseOperateFlag,
            data?: IBaseOperateData,
        ) => void,
        weight = 0,
    ) {
        return this.eventBus.on(this.getTranslateEventName(), callback, weight);
    }

    /** 触发移动/旋转事件 */
    emitTranslate(layerId: string, operateType: BaseLayerViewType, args: IBaseOperateFlag, data?: IBaseOperateData) {
        this.eventBus.emit(this.getTranslateEventName(), layerId, operateType, args, data);
    }

    /** 清除事件 */
    off(handle: number) {
        this.eventBus.off(handle);
    }
    /** 清除事件 */
    offEvent(handle: number) {
        this.off(handle);
    }
    /**
     * 监听唯一事件
     *
     * @param eventName - 事件名称
     * @param callback - 回调函数
     */
    onUnique(eventName: string, callback: (...args: any[]) => void) {
        return this.eventBus.onUnique(eventName, callback);
    }
    /** 监听事件 */
    on(eventName: string, callback: (...args: any[]) => void, weight = 0) {
        return this.eventBus.on(eventName, callback, weight);
    }
    /** 监听事件 */
    onEvent(eventName: string, callback: (...args: any[]) => void, weight = 0) {
        return this.on(eventName, callback, weight);
    }
    /** 触发事件 */
    emit(eventName: string, ...args: any[]) {
        this.eventBus.emit(eventName, ...args);
    }
    /** 触发事件 */
    emitEvent(eventName: string, ...args: any[]) {
        this.emit(eventName, ...args);
    }

    private getLayerClickEventName() {
        return `gs-layer-click`;
    }
    /** 监听图层点击事件 */
    onLayerClick(callback: (layerId: string, point: IPoint) => void, weight = 0) {
        return this.eventBus.on(this.getLayerClickEventName(), callback, weight);
    }
    /** 触发图层点击事件 */
    emitLayerClick(layerId: string, point: IPoint) {
        this.eventBus.emit(this.getLayerClickEventName(), layerId, point);
    }

    private getLayerDoubleClickEventName() {
        return `gs-layer-double-click`;
    }
    /** 监听图层双击事件 */
    onLayerDoubleClick(callback: (layerId: string, point: IPoint) => void, weight = 0) {
        return this.eventBus.on(this.getLayerDoubleClickEventName(), callback, weight);
    }
    /** 触发图层双击事件 */
    emitLayerDoubleClick(layerId: string, point: IPoint) {
        this.eventBus.emit(this.getLayerDoubleClickEventName(), layerId, point);
    }

    private getGlobalMaskEventName() {
        return `gs-global-mask`;
    }
    /** 监听全局遮罩事件 */
    onGlobalMask(
        callback: (args: { isShow: boolean; isHide: boolean; zIndex: number; data: any }) => void,
        weight = 0,
    ) {
        return this.eventBus.on(this.getGlobalMaskEventName(), callback, weight);
    }
    /** 触发全局遮罩事件 */
    emitGlobalMask(args: { isShow: boolean; isHide: boolean; zIndex: number; data: any }) {
        this.eventBus.emit(this.getGlobalMaskEventName(), args);
    }

    private getLayerChangeEventName(layerId = '') {
        return `gs-layer-change-${layerId}`;
    }
    /** 监听图层更新事件 */
    onLayerChange(callback: () => void, layerId?: string, weight = 0) {
        return this.eventBus.on(this.getLayerChangeEventName(layerId), callback, weight);
    }
    /** 触发图层更新事件 */
    emitLayerChange(layerId?: string) {
        // 触发所有图层更新事件
        this.eventBus.emit(this.getLayerChangeEventName());
        // 触发指定图层更新事件
        if (layerId) {
            this.eventBus.emit(this.getLayerChangeEventName(layerId));
        }
    }

    private getDropLayerEventName() {
        return `gs-drop-layer`;
    }
    /** 监听图层拖拽事件 */
    onDropLayer(callback: (e: React.DragEvent<HTMLDivElement>) => void, weight = 0) {
        return this.eventBus.on(this.getDropLayerEventName(), callback, weight);
    }
    /** 触发图层拖拽事件 */
    emitDropLayer(e: React.DragEvent<HTMLDivElement>) {
        this.eventBus.emit(this.getDropLayerEventName(), e);
    }

    private getDragOverLayerEventName() {
        return `gs-drag-over-layer`;
    }
    /** 触发图层拖拽事件 */
    emitDragOverLayer(e: React.DragEvent<HTMLDivElement>) {
        this.eventBus.emit(this.getDragOverLayerEventName(), e);
    }

    /** 监听图层拖拽事件 */
    onDragOverLayer(callback: (e: React.DragEvent<HTMLDivElement>) => void, weight = 0) {
        return this.eventBus.on(this.getDragOverLayerEventName(), callback, weight);
    }

    private getDragLeaveLayerEventName() {
        return `gs-drag-leave-layer`;
    }
    /** 监听图层拖拽事件 */
    onDragLeaveLayer(callback: (e: React.DragEvent<HTMLDivElement>) => void, weight = 0) {
        return this.eventBus.on(this.getDragLeaveLayerEventName(), callback, weight);
    }
    /** 触发图层拖拽事件 */
    emitDragLeaveLayer(e: React.DragEvent<HTMLDivElement>) {
        this.eventBus.emit(this.getDragLeaveLayerEventName(), e);
    }

    private getActiveStageChangeEventName() {
        return `gs-active-stage-index-change`;
    }
    /** 监听激活舞台索引变化事件 */
    onActiveStageChange(callback: (args: { stageIndex: number; originIndex: number }) => void, weight = 0) {
        return this.eventBus.on(this.getActiveStageChangeEventName(), callback, weight);
    }
    /** 触发激活舞台索引变化事件 */
    emitActiveStageChange(args: { stageIndex: number; originIndex: number }) {
        this.eventBus.emit(this.getActiveStageChangeEventName(), args);
    }

    /** 舞台滚动事件名称 */
    private stageScrollTopEventName = 'gs-stage-scroll-top';
    // @TODO stageManager/views/resizeStageOperate.tsx 发送事件搬迁
    // @TODO stageManager/views/scaleStage.tsx 接收事件搬迁
    /** 触发舞台滚动事件 */
    emitStageScrollTop() {
        this.eventBus.emit(this.stageScrollTopEventName);
    }
    /** 监听舞台滚动事件 */
    onStageScrollTop(callback: () => void, weight = 0) {
        return this.eventBus.on(this.stageScrollTopEventName, callback, weight);
    }

    private getPickStageChangeEventName() {
        return `gs-pick-stage-change`;
    }
    /** 监听选中舞台索引变化事件 */
    onPickStageChange(callback: (isPickStage: boolean) => void, weight = 0) {
        return this.eventBus.on(this.getPickStageChangeEventName(), callback, weight);
    }
    /** 触发选中舞台索引变化事件 */
    emitPickStageChange(isPickStage: boolean) {
        this.eventBus.emit(this.getPickStageChangeEventName(), isPickStage);
    }

    private getStageSelectionChangeEventName() {
        return `gs-stage-selection-change`;
    }
    /** 监听选中舞台索引变化事件 */
    onStageSelectionChange(callback: (indexes: number[]) => void, weight = 0) {
        return this.eventBus.on(this.getStageSelectionChangeEventName(), callback, weight);
    }
    /** 触发选中舞台索引变化事件 */
    emitStageSelectionChange(indexes: number[]) {
        this.eventBus.emit(this.getStageSelectionChangeEventName(), indexes);
    }

    private getStageTypeChangeEventName() {
        return `gs-stage-Type-change`;
    }
    /**监听舞台类型变化事件 */
    onStageTypeChange(callback: (unit: StageType) => void, weight = 0) {
        return this.eventBus.on(this.getStageTypeChangeEventName(), callback, weight);
    }
    /** 触发舞台类型变化事件 */
    emitStageTypeChange(stageType: StageType) {
        this.eventBus.emit(this.getStageTypeChangeEventName(), stageType);
    }

    private getStageUnitChangeEventName() {
        return `gs-stage-unit-change`;
    }
    /**监听舞台单位变化事件 */
    onStageUnitChange(callback: (unit: SizeUnitType) => void, weight = 0) {
        return this.eventBus.on(this.getStageUnitChangeEventName(), callback, weight);
    }
    /** 触发舞台单位变化事件 */
    emitStageUnitChange(unit: SizeUnitType) {
        this.eventBus.emit(this.getStageUnitChangeEventName(), unit);
    }

    private getShineEventName(type: 'show' | 'hide' = 'show') {
        return `gs-shine-${type}`;
    }
    /** 监听闪烁事件 */
    onLayerShine(callback: (layerId: string, duration: number) => void, weight = 0) {
        return this.eventBus.on(this.getShineEventName(), callback, weight);
    }
    /** 触发闪烁事件 */
    emitLayerShine(layerId: string, duration: number) {
        this.eventBus.emit(this.getShineEventName(), layerId, duration);
    }
    /** 监听闪烁隐藏事件 */
    onLayerShineHide(callback: (layerId: string) => void, weight = 0) {
        return this.eventBus.on(this.getShineEventName('hide'), callback, weight);
    }
    /** 触发闪烁隐藏事件 */
    emitLayerShineHide(layerId: string) {
        this.eventBus.emit(this.getShineEventName('hide'), layerId);
    }

    private getEditorOperateStateEventName() {
        return `gs-editor-operate-state`;
    }
    /** 监听编辑器状态 */
    onEditorOperateState(
        callback: (args: {
            disableOperate: boolean;
            disablePick: boolean;
            disableMouseSelect: boolean;
            disableMove: boolean;
            disableScale: boolean;
            disableRotate: boolean;
            disableResize: boolean;
            isBaseOperate: boolean;
            isMouseSelect: boolean;
        }) => void,
        weight = 0,
    ) {
        return this.eventBus.on(this.getEditorOperateStateEventName(), callback, weight);
    }
    /** 触发编辑器状态 */
    emitEditorOperateState(args: {
        disableOperate: boolean;
        disablePick: boolean;
        disableMouseSelect: boolean;
        disableMove: boolean;
        disableScale: boolean;
        disableRotate: boolean;
        disableResize: boolean;
        isBaseOperate: boolean;
        isMouseSelect: boolean;
        disableActive: boolean;
    }) {
        this.eventBus.emit(this.getEditorOperateStateEventName(), args);
    }

    private getBehaviorErrorImageEventName() {
        return `gs-behavior-error-image`;
    }

    /** 监听行为错误图片事件 */
    onBehaviorErrorImage(callback: (error: { layerId: string; imageUrl: string }, url: string) => void, weight = 0) {
        return this.eventBus.on(this.getBehaviorErrorImageEventName(), callback, weight);
    }

    /**
     * 上报图片合成失败
     *
     * @param layerId - 图层 ID
     * @param imageUrl - 图片链接地址
     */
    emitBehaviorErrorImage(error: { layerId: string; imageUrl: string }) {
        this.eventBus.emit(this.getBehaviorErrorImageEventName(), error);
    }

    private getShortcutEventName() {
        return `gs-shortcut`;
    }

    /** 监听快捷菜单事件 */
    onShortcutEvent(callback: (func: string) => void, weight = 0) {
        return this.eventBus.on(this.getShortcutEventName(), callback, weight);
    }
    /** 触发快捷菜单事件 */
    emitShortcutEvent(func: string) {
        this.eventBus.emit(this.getShortcutEventName(), func);
    }
}
